import {
  Component,
  ErrorInfo,
  PropsWithChildren,
  ReactNode,
  Suspense,
} from 'react';
import { RouteObject } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { RouteConfig, SyncHook } from './route-types';
import RouteLoadingFallback from './RouteLoadingFallback';

// Error boundary for route loading errors
interface RouteErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class RouteErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  RouteErrorBoundaryState
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Route loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Failed to load route</h2>
            <p>{this.state.error?.message || 'Unknown error'}</p>
            <button onClick={() => window.location.reload()}>
              Reload Page
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

// Wrap component with Suspense and Error Boundary
// Accept ReactNode to match RouteObject.element type
const wrapWithSuspense = (element: ReactNode) => (
  <RouteErrorBoundary>
    <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
  </RouteErrorBoundary>
);

const getSyncHookWrapper =
  (useSyncHook: SyncHook) =>
  ({ children }: PropsWithChildren) => {
    useSyncHook();
    return <>{children}</>;
  };

/**
 * getAppRoutesFromConfigs returns the final route list from a set of routeConfigs.
 * For some routes in our application we want to sync the route information with the state,
 * and for those routes we create a "useSyncHook" where we put that sync logic. When there is a sync hook we want to
 * wrap the element with the SyncHookWrapper which will ensure the useSyncHook is ran for those routes.
 * @param routeConfigs configurations for different sets of routes in our app.
 * @returns RouteObject[] ready for react-router
 */
export const getAppRoutesFromConfigs = (routeConfigs: RouteConfig[]) => {
  return routeConfigs.reduce((prevRoutes, routeConfig) => {
    // Wrap all route elements with Suspense to handle lazy loading
    let subRoutes: RouteObject[] = routeConfig.routes.map(route => ({
      ...route,
      element: wrapWithSuspense(route.element),
    }));

    // If the routeConfig has a useSyncHook attached to it then wrap the element with the SyncHookWrapper that runs the useSyncHook for that route.
    if (routeConfig.useSyncHook) {
      const SyncHookWrapper = getSyncHookWrapper(routeConfig.useSyncHook);
      subRoutes = subRoutes.reduce((prevSubRoutes, subRoute) => {
        const newSubRoute = {
          ...subRoute,
          element: <SyncHookWrapper>{subRoute.element}</SyncHookWrapper>,
        };
        return [...prevSubRoutes, newSubRoute];
      }, [] as RouteObject[]);
    }

    if (!routeConfig.publicRoute) {
      subRoutes = subRoutes.reduce((prevSubRoutes, subRoute) => {
        const newSubRoute = {
          ...subRoute,
          element: <ProtectedRoute>{subRoute.element}</ProtectedRoute>,
        };
        return [...prevSubRoutes, newSubRoute];
      }, [] as RouteObject[]);
    }
    return [...prevRoutes, ...subRoutes];
  }, [] as RouteObject[]);
};
